import React, { useEffect, useState } from "react";
import api, { API, getToken } from "../../lib/api";
import { CheckCircle2, Trash2 } from "lucide-react";
import { toast } from "sonner";

const PRIO = { urgent: "bg-red-600 text-white", high: "bg-amber-500 text-white", normal: "bg-stone-100 text-stone-600 border border-stone-200", low: "bg-stone-100 text-stone-400 border border-stone-200" };
const STATUS = { open: "bg-stone-200 text-stone-700", scheduled: "bg-[#1F3D34] text-white", in_progress: "bg-amber-500 text-white", complete: "bg-[#7C9673] text-white" };

export function EstateImg({ tid, pid, className }) {
  const [url, setUrl] = useState(null);
  useEffect(() => {
    let revoked = null;
    (async () => {
      try {
        const res = await fetch(`${API}/estate/tasks/${tid}/photos/${pid}/image`, { headers: { Authorization: `Bearer ${getToken()}` }, credentials: "include" });
        if (res.ok) { revoked = URL.createObjectURL(await res.blob()); setUrl(revoked); }
      } catch (e) { console.warn("EstateImg load failed", e); }
    })();
    return () => { if (revoked) URL.revokeObjectURL(revoked); };
  }, [tid, pid]);
  if (!url) return <div className={`bg-stone-100 animate-pulse ${className}`} />;
  return <img src={url} alt="task" className={className} />;
}

export default function EstateTasks({ properties, employees, isManager }) {
  const [rows, setRows] = useState(null);
  const [filters, setFilters] = useState({ property_id: "", status: "open,scheduled,in_progress", employee_id: "" });

  const load = () => {
    const p = new URLSearchParams();
    Object.entries(filters).forEach(([k, v]) => v && p.set(k, v));
    api.get(`/estate/tasks?${p}`).then(({ data }) => setRows(data));
  };
  useEffect(() => { load(); }, [filters]); // eslint-disable-line react-hooks/exhaustive-deps

  const update = async (t, upd) => {
    try { await api.put(`/estate/tasks/${t.id}`, upd); load(); }
    catch (e) { toast.error("Update failed"); }
  };
  const remove = async (t) => {
    if (!window.confirm("Delete this task?")) return;
    await api.delete(`/estate/tasks/${t.id}`); load();
  };

  const S = "h-10 px-2 border border-stone-300 text-sm bg-white";
  return (
    <div className="space-y-4" data-testid="estate-tasks">
      <div className="flex flex-wrap gap-2">
        <select data-testid="tasks-filter-property" value={filters.property_id} onChange={(e) => setFilters({ ...filters, property_id: e.target.value })} className={S}>
          <option value="">All Properties</option>
          {properties.map((p) => <option key={p.id} value={p.id}>{p.name}</option>)}
        </select>
        <select data-testid="tasks-filter-status" value={filters.status} onChange={(e) => setFilters({ ...filters, status: e.target.value })} className={S}>
          <option value="open,scheduled,in_progress">Active</option>
          <option value="open">Open (unscheduled)</option>
          <option value="scheduled">Scheduled</option>
          <option value="complete">Completed</option>
        </select>
        <select data-testid="tasks-filter-employee" value={filters.employee_id} onChange={(e) => setFilters({ ...filters, employee_id: e.target.value })} className={S}>
          <option value="">All Employees</option>
          {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
        </select>
      </div>

      <div className="space-y-2" data-testid="estate-task-list">
        {(rows || []).map((t) => (
          <div key={t.id} className="bg-white border border-stone-200 p-4" data-testid={`estate-task-${t.id}`}>
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${PRIO[t.priority] || PRIO.normal}`}>{t.priority}</span>
                  <span className="font-bold">{t.title}</span>
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${STATUS[t.status] || STATUS.open}`}>{t.status.replace("_", " ")}</span>
                  {t.source === "schedule" && <span className="text-[10px] font-bold uppercase px-2 py-0.5 bg-[#7C9673]/20 text-[#1F3D34]">Recurring</span>}
                </div>
                <div className="text-xs text-stone-500 mt-1">
                  {t.property_name} · {t.category}{t.scheduled_date ? ` · ${t.scheduled_date}` : ""}{t.assigned_to ? ` · ${t.assigned_to}` : " · unassigned"}
                  {t.status === "complete" && t.completed_by ? ` · done by ${t.completed_by}` : ""}
                </div>
                {t.notes && <div className="text-xs text-stone-500 mt-1 italic">{t.notes}</div>}
                {(t.photos || []).length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {t.photos.map((p) => <EstateImg key={p.id} tid={t.id} pid={p.id} className="h-20 w-20 object-cover border border-stone-200" />)}
                  </div>
                )}
              </div>
              {t.status !== "complete" && (
                <div className="flex items-center gap-2 flex-wrap">
                  <select data-testid={`assign-${t.id}`} value={t.assigned_to_id || ""} onChange={(e) => update(t, { assigned_to_id: e.target.value })} className="h-9 px-2 border border-stone-300 text-xs">
                    <option value="">Assign…</option>
                    {employees.map((emp) => <option key={emp.id} value={emp.id}>{emp.name}</option>)}
                  </select>
                  <input data-testid={`schedule-${t.id}`} type="date" value={t.scheduled_date || ""} onChange={(e) => update(t, { scheduled_date: e.target.value })} className="h-9 px-2 border border-stone-300 text-xs" />
                  <button data-testid={`complete-${t.id}`} onClick={() => update(t, { status: "complete" })}
                    className="h-9 px-3 bg-[#1F3D34] text-white font-bold uppercase text-[10px] flex items-center gap-1"><CheckCircle2 size={12} /> Done</button>
                  {isManager && <button data-testid={`delete-${t.id}`} onClick={() => remove(t)} className="text-stone-300 hover:text-red-500"><Trash2 size={15} /></button>}
                </div>
              )}
            </div>
          </div>
        ))}
        {rows && rows.length === 0 && (
          <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm" data-testid="estate-tasks-empty">No tasks match</div>
        )}
      </div>
    </div>
  );
}
